import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import CloseIcon from '@mui/icons-material/Close'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { useConfiguratorConfigOptional } from '~/context/urlConfigContext'
import { useLocale } from '~/hooks/useLocale'
import { buildConfigShareUrl } from '~/utils/configShareUrl'
import type { ProductKey } from '~/utils/configUrl'
import { trackPromoPopup } from '~/utils/analytics'
import { promoChatBus } from './promoChatBus'
import { usePromoPopupTrigger, DISMISSED_KEY } from './usePromoPopupTrigger'
import styles from './PromoLeadPopup.module.css'

// Converts an engaged-but-stalled configurator visitor into a webchat lead:
// shows a bottom sheet during a pause with a phone input; submitting fires the
// lead through the webchat (project link included) and the chat opens with an
// instant auto-reply carrying the same link.
export const PromoLeadPopup: React.FC<{
  product?: ProductKey
  /** Catalog-style pages: count sustained scrolling as engagement. */
  scrollEngagement?: boolean
}> = ({ product, scrollEngagement }) => {
  const intl = useIntl()
  const locale = useLocale()
  const ctx = useConfiguratorConfigOptional()
  const [phone, setPhone] = useState('')
  const phoneInputRef = useRef<HTMLInputElement>(null)

  const configJson =
    ctx && product ? JSON.stringify(ctx.config) : null
  const { visible, hide } = usePromoPopupTrigger(
    configJson,
    scrollEngagement ?? false
  )

  useEffect(() => {
    promoChatBus.state.popupVisible = visible
    if (visible) {
      trackPromoPopup('popup_shown', { pagePath: window.location.pathname })
    }
    return () => {
      promoChatBus.state.popupVisible = false
    }
  }, [visible])

  // Only the explicit ✕ closes the popup; it starts the 7-day cooldown so it
  // won't re-show for a week. Tapping outside the sheet no longer dismisses it.
  const handleClose = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    } catch {
      /* ignore */
    }
    trackPromoPopup('popup_dismissed', { pagePath: window.location.pathname })
    hide()
  }

  if (!visible) return null

  // The button stays visually active: MUI's .Mui-disabled washes the label
  // into the pill on real devices. An empty tap just focuses the input.
  const handleSubmit = () => {
    if (phone.trim().length === 0) {
      phoneInputRef.current?.focus()
      return
    }
    const projectUrl =
      ctx && product
        ? buildConfigShareUrl(ctx.config, product, locale)
        : window.location.href
    trackPromoPopup('popup_cta_click', { pagePath: window.location.pathname })
    hide()
    promoChatBus.request({ projectUrl, phone: phone.trim() })
  }

  return (
    <div
      className={styles.sheet}
      role="dialog"
      aria-modal="false"
      aria-labelledby="promo-popup-title"
      data-testid="promo-popup"
    >
      <button
        type="button"
        className={styles.closeButton}
        aria-label={intl.formatMessage({ id: 'promoPopup.close' })}
        onClick={handleClose}
        data-testid="promo-popup-close"
      >
        <CloseIcon fontSize="inherit" />
      </button>
      {/* Before/after crossfade of the same room frame — the "your wardrobe
          in your interior" magnet. Fixed-height box + explicit img sizes:
          zero layout shift; eager + high priority since it's the popup hero. */}
      <div className={styles.mediaBox} aria-hidden="true">
        <img
          className={styles.mediaFrame}
          src="/cdn/promo/promo-before.webp"
          alt=""
          width={800}
          height={1192}
          fetchPriority="high"
          decoding="async"
        />
        <div className={styles.mediaAfter}>
          <img
            className={styles.mediaFrame}
            src="/cdn/promo/promo-after.webp"
            alt=""
            width={400}
            height={596}
            fetchPriority="high"
            decoding="async"
          />
          <span className={styles.mediaBadge}>
            <FormattedMessage id="promoPopup.badge3d" />
          </span>
        </div>
      </div>
      <div className={styles.content}>
        <h3 id="promo-popup-title" className={styles.title}>
          <FormattedMessage id="promoPopup.title" />
        </h3>
        <p className={styles.body}>
          <FormattedMessage id="promoPopup.body" />
        </p>
        <input
          ref={phoneInputRef}
          className={styles.phoneInput}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          placeholder={intl.formatMessage({
            id: 'promoPopup.phonePlaceholder',
          })}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) =>
            e.key === 'Enter' && (e.preventDefault(), handleSubmit())
          }
          data-testid="promo-popup-phone"
        />
        <p className={styles.consent}>
          <FormattedMessage id="promoPopup.consent" />
        </p>
        <div className={styles.ctaRow}>
          <CustomButton
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            data-testid="promo-popup-cta"
          >
            <FormattedMessage id="promoPopup.cta" />
          </CustomButton>
        </div>
      </div>
    </div>
  )
}
